/**
 * Helpers cliente — duplicado defensivo de la lógica del backend para feedback inmediato.
 * El backend revalida todo. Si hay discrepancia, gana el backend.
 */

export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unknown';

export const detectBrand = (pan: string): CardBrand => {
  const n = pan.replace(/[\s-]/g, '');
  if (n.startsWith('4')) return 'visa';
  if (/^(5[1-5]|2(2[2-9]|[3-6][0-9]|7[01]|720))/.test(n)) return 'mastercard';
  if (/^3[47]/.test(n)) return 'amex';
  if (/^(6011|65|64[4-9])/.test(n)) return 'discover';
  if (/^(30[0-5]|3[68])/.test(n)) return 'diners';
  if (/^35(2[89]|[3-8])/.test(n)) return 'jcb';
  return 'unknown';
};

export const luhnCheck = (pan: string): boolean => {
  if (typeof pan !== 'string' || !pan) return false;
  if (!/^[\d\s-]+$/.test(pan)) return false;
  const digits = pan.replace(/[\s-]/g, '');
  if (digits.length < 12 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    const n = digits.charCodeAt(i) - 48;
    if (n < 0 || n > 9) return false;
    let d = n;
    if (alt) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    alt = !alt;
  }
  return sum % 10 === 0;
};

/** Formatea el PAN con espacios cada 4 dígitos (Amex: 4-6-5). */
export const formatPan = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  const brand = detectBrand(digits);
  if (brand === 'amex') {
    return digits
      .slice(0, 15)
      .replace(/^(\d{4})(\d{0,6})(\d{0,5}).*$/, (_m, a, b, c) =>
        [a, b, c].filter(Boolean).join(' '),
      );
  }
  return digits
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ');
};

/** Formatea MM/YY o MM/YYYY para el campo de expiración. */
export const formatExpiry = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

/** Parsea expMonth/expYear desde "MM/YY" o "MM/YYYY". */
export const parseExpiry = (
  raw: string,
): { ok: true; month: number; year: number } | { ok: false; error: string } => {
  const cleaned = raw.replace(/\s/g, '');
  const m = /^(\d{1,2})\/(\d{2}|\d{4})$/.exec(cleaned);
  if (!m) return { ok: false, error: 'Formato MM/YY o MM/YYYY' };
  const month = Number(m[1]);
  let year = Number(m[2]);
  if (year < 100) year = 2000 + year;
  if (month < 1 || month > 12) return { ok: false, error: 'Mes inválido (1-12)' };
  const now = new Date();
  if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
    return { ok: false, error: 'Tarjeta expirada' };
  }
  return { ok: true, month, year };
};
